import { createSlice } from "@reduxjs/toolkit";
import * as Sentry from "@sentry/react";
import jwtDecode from "jwt-decode";
import { callApi } from "src/utils/apiCall";
import { getCached, setCached, STATIC_LOOKUP_CACHE_TTL_MS } from "src/utils/staticLookupCache";
import { dispatch, store } from "../store";
import { updateError } from "./user";

const INITIAL_STATE = {
    mergeTagKeys: [],
    mtValPractice: {},
    mtValTreatments: {},
    mtValLeads: {},
    mtValUser: {},
    mergeTagKeysLoading: false,
    mergeTagKeysError: null,
    mtValLoadingPractice: false,
    mtValLoadingLeads: false,
    mtValLoadingTreatments: false,
    mtValLoadingUser: false,
    settingsMenuActive: false,
    selectedPracticeForMenu: "practice_details",
    practiceUsers: [],
}

const slice = createSlice({
    name: "mergeTags",
    initialState: INITIAL_STATE,
    reducers: {
        reset(state) {
            Object.assign(state, INITIAL_STATE);
        },
        resetTreatmentAndLead(state) {
            state.mtValLoadingLeads = false;
            state.mtValLeads = {};
            state.mtValLoadingTreatments = false;
            state.mtValTreatments = {};
        },
        fetchMergeTagKeys(state) {
            state.mergeTagKeysLoading = true;
            state.mergeTagKeysError = null;
        },
        storeMergeTagKey(state, action) {
            state.mergeTagKeysLoading = false;
            state.mergeTagKeysError = null;
            state.mergeTagKeys = action?.payload;
        },
        failMergeTagKeys(state, action) {
            state.mergeTagKeysLoading = false;
            state.mergeTagKeysError = action?.payload || 'Unable to load merge tags.';
        },
        fetchMergeTagValueForPractice(state) {
            state.mtValLoadingPractice = true;
            state.mtValPractice = {};
        },
        fetchMergeTagValueForLead(state) {
            state.mtValLoadingLeads = true;
            state.mtValLeads = {};
        },
        fetchMergeTagValueForTreatment(state) {
            state.mtValLoadingTreatments = true;
            state.mtValTreatments = {};
        },
        fetchMergeTagValueForUser(state) {
            state.mtValLoadingUser = true;
            state.mtValUser = {};
        },
        storeMergeTagValueForPractice(state, action) {
            state.mtValLoadingPractice = false;
            state.mtValPractice = action?.payload;
        },
        storeMergeTagValueForLead(state, action) {
            state.mtValLoadingLeads = false;
            state.mtValLeads = action?.payload;
        },
        storeMergeTagValueForTreatment(state, action) {
            state.mtValLoadingTreatments = false;
            state.mtValTreatments = action?.payload;
        },
        storeMergeTagValueForUser(state, action) {
            state.mtValLoadingUser = false;
            state.mtValUser = action?.payload;
        },
        updateSettingsMenuActive(state, action) {
            state.settingsMenuActive = action.payload;
        },
        updateSelectedPracticeForMenu(state, action) {
            state.selectedPracticeForMenu = action.payload
        },
        updatePracticeUsers(state, action) {
            state.practiceUsers = action.payload
        },
    }
});

export default slice.reducer;
export const { reset, resetTreatmentAndLead, updateSettingsMenuActive, updateSelectedPracticeForMenu } = slice.actions;

const getPracticeId = () => {
    const id = store.getState()?.practiceTreatmentList?.practiceId;
    if (!id || id === 'null' || id === 'undefined') return null;
    return id;
};

// Best-effort current-user id for scoping the get_context/auth/user cache key.
// JWTContext keeps the authenticated user in its own React context (not
// Redux), so a plain thunk file can't read it from the store — decode it off
// the access token instead, same pattern already used by authSessionTelemetry.js
// and jwt.js. Returns null (never throws) if there's no token or it's malformed;
// callers treat null as "don't cache this response".
const getUserId = () => {
    try {
        const token = localStorage.getItem('accessToken');
        if (!token) return null;
        const decoded = jwtDecode(token);
        return decoded?.user_id ?? decoded?.userId ?? decoded?.sub ?? null;
    } catch (_e) {
        return null;
    }
};

// Helper: extract a useful error message from a failed API response,
// log it to Sentry as a breadcrumb (always) and as a warning-level
// event for 5xx/network failures (so we have visibility without noise).
const _mergeTagError = (endpoint, response, caught) => {
    const status = response?.status || caught?.response?.status || 0;
    const detail = response?.data?.detail || response?.data?.error || response?.data?.message
        || caught?.data?.message || caught?.response?.data?.message || caught?.message;
    const msg = detail ? `${detail} (${endpoint})` : `Request failed for ${endpoint} (HTTP ${status})`;
    console.error(`[mergeTags] ${msg}`, caught || response);

    // Always add a Sentry breadcrumb — visible in the breadcrumb trail
    // of ANY subsequent error event in this user's session.
    Sentry.addBreadcrumb({
        category: 'merge-tag-fetch',
        message: msg,
        level: status >= 500 || status === 0 ? 'error' : 'warning',
        data: { endpoint, status, detail: detail || null },
    });

    // For 5xx and network failures (status 0), capture a Sentry warning
    // so we can track systematic endpoint failures. 4xx (403, 404) are
    // expected states (no permission, deleted lead) and don't need capture.
    if (status >= 500 || status === 0) {
        Sentry.captureMessage(`Merge tag fetch failed: ${endpoint} (HTTP ${status})`, {
            level: 'warning',
            tags: { endpoint, http_status: String(status) },
            extra: { detail, responseData: response?.data },
        });
    }

    return msg;
};

// Cache key for the global (not practice/user-scoped) merge-tag key list.
const MERGE_TAG_KEYS_CACHE_KEY = 'mergetags';

// Cache key for a practice's get_context merge-tag payload
// (fetchMergeTagValueForPractice / GET /practices/{id}/get_context/). Exported
// so any write path that changes fields surfaced through get_context (e.g.
// updatePractice in src/redux/slices/practice.js) can invalidate the same
// entry without duplicating the string literal.
export const getPracticeContextCacheKey = (practiceId) => `get_context:practice:${practiceId}`;

export function fetchMergeTagKeys() {
    return async () => {
        try {
            dispatch(slice?.actions?.fetchMergeTagKeys());
            const cached = getCached(MERGE_TAG_KEYS_CACHE_KEY, { ttlMs: STATIC_LOOKUP_CACHE_TTL_MS });
            if (cached !== undefined) {
                dispatch(slice?.actions?.storeMergeTagKey(cached));
                return;
            }
            const response = await callApi(`/api/v1/utils/mergetags/`);
            if (response?.status === 200) {
                const STATIC_TAGS = ['consent_form_dynamic_url'];
                const apiTags = response?.data?.map(el => el?.tag) || [];
                const massagedData = [...new Set([...apiTags, ...STATIC_TAGS])].sort();
                dispatch(slice?.actions?.storeMergeTagKey(massagedData));
                setCached(MERGE_TAG_KEYS_CACHE_KEY, massagedData);
            } else {
                const msg = _mergeTagError('/utils/mergetags/', response);
                dispatch(slice?.actions?.failMergeTagKeys(msg));
                dispatch(updateError({ success: "warning", message: msg }));
            }
        } catch (e) {
            const msg = _mergeTagError('/utils/mergetags/', null, e);
            dispatch(slice?.actions?.failMergeTagKeys(msg));
            dispatch(updateError({ success: "warning", message: msg }));
        }
    }
}

export function fetchMergeTagValueForPractice() {
    return async () => {
        try {
            dispatch(slice?.actions?.fetchMergeTagValueForPractice());
            const practiceId = getPracticeId();
            if (!practiceId) return;
            const cacheKey = getPracticeContextCacheKey(practiceId);
            const cached = getCached(cacheKey, { ttlMs: STATIC_LOOKUP_CACHE_TTL_MS });
            if (cached !== undefined) {
                dispatch(slice?.actions?.storeMergeTagValueForPractice(cached));
                return;
            }
            const response = await callApi(`/api/v1/practices/${practiceId}/get_context/`);
            if (response?.status === 200) {
                dispatch(slice?.actions?.storeMergeTagValueForPractice(response?.data ?? {}));
                setCached(cacheKey, response?.data ?? {});
            } else {
                dispatch(slice?.actions?.storeMergeTagValueForPractice({}));
                const msg = _mergeTagError(`/practices/${practiceId}/get_context/`, response);
                dispatch(updateError({ success: "warning", message: msg }));
            }
        } catch (e) {
            dispatch(slice?.actions?.storeMergeTagValueForPractice({}));
            dispatch(updateError({ success: "warning", message: _mergeTagError('/practices/get_context/', null, e) }));
        }
    }
}

export function fetchPracticeUsers() {
    return async () => {
        try {
            dispatch(slice?.actions?.updatePracticeUsers([]));
            const practiceId = getPracticeId();
            if (!practiceId) return;
            // Use /users/ endpoint instead of /reports/practice-user/ — the reports
            // endpoint requires HasFeaturePermission('analytics-reports') which not
            // all practices have, causing 403 errors on every dashboard load.
            const response = await callApi(`/api/v1/practices/${practiceId}/users/?fields={id,role,user{first_name,last_name}}`);
            if (response?.status === 200) {
                const results = response?.data?.results || response?.data || [];
                const data = results
                    .filter(obj => obj?.role === "Practitioner")
                    .map(obj => {
                        const practicionerName = `${obj?.user?.first_name ?? ""} ${obj?.user?.last_name ?? ""}`;
                        return ({
                            label: practicionerName?.trim(),
                            value: practicionerName?.toUpperCase()?.trim(),
                            icon: "fluent-emoji-high-contrast:health-worker",
                            id: obj?.id
                        })
                    });
                dispatch(slice?.actions?.updatePracticeUsers(data ?? []));
            } else {
                dispatch(slice?.actions?.updatePracticeUsers([]));
                const msg = _mergeTagError(`/practices/${practiceId}/users/`, response);
                dispatch(updateError({ success: "warning", message: msg }));
            }
        } catch (e) {
            dispatch(slice?.actions?.updatePracticeUsers([]));
            dispatch(updateError({ success: "warning", message: _mergeTagError('/practices/users/', null, e) }));
        }
    }
}

export function fetchMergeTagValueForUser() {
    return async () => {
        try {
            dispatch(slice?.actions?.fetchMergeTagValueForUser());
            const userId = getUserId();
            const cacheKey = userId != null ? `get_context:user:${userId}` : null;
            if (cacheKey) {
                const cached = getCached(cacheKey, { ttlMs: STATIC_LOOKUP_CACHE_TTL_MS });
                if (cached !== undefined) {
                    dispatch(slice?.actions?.storeMergeTagValueForUser(cached));
                    return;
                }
            }
            const response = await callApi(`/api/v1/auth/user/get_context/`);
            if (response?.status === 200) {
                dispatch(slice?.actions?.storeMergeTagValueForUser(response?.data ?? {}));
                if (cacheKey) setCached(cacheKey, response?.data ?? {});
            } else {
                dispatch(slice?.actions?.storeMergeTagValueForUser({}));
                const msg = _mergeTagError('/auth/user/get_context/', response);
                dispatch(updateError({ success: "warning", message: msg }));
            }
        } catch (e) {
            dispatch(slice?.actions?.storeMergeTagValueForUser({}));
            dispatch(updateError({ success: "warning", message: _mergeTagError('/auth/user/get_context/', null, e) }));
        }
    }
}

export function fetchMergeTagValueForLeads(leadId) {
    return async () => {
        try {
            if (!leadId) {
                dispatch(slice?.actions?.storeMergeTagValueForLead({}));
                return;
            }
            dispatch(slice?.actions?.fetchMergeTagValueForLead());
            const practiceId = getPracticeId();
            if (!practiceId) return;
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/get_context/`);
            if (response?.status === 200) {
                dispatch(slice?.actions?.storeMergeTagValueForLead(response?.data ?? {}));
            } else {
                dispatch(slice?.actions?.storeMergeTagValueForLead({}));
                const msg = _mergeTagError(`/leads/${leadId}/get_context/`, response);
                dispatch(updateError({ success: "warning", message: msg }));
            }
        } catch (e) {
            dispatch(slice?.actions?.storeMergeTagValueForLead({}));
            dispatch(updateError({ success: "warning", message: _mergeTagError('/leads/get_context/', null, e) }));
        }
    }
}

export function fetchMergeTagValueForTreatments(leadId, treatmentId) {
    return async () => {
        try {
            if (!(leadId && treatmentId)) {
                dispatch(slice?.actions?.storeMergeTagValueForTreatment({}));
                return;
            }
            dispatch(slice?.actions?.fetchMergeTagValueForTreatment());
            const practiceId = getPracticeId();
            if (!practiceId) return;
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/treatments/${treatmentId}/get_context/`);
            if (response?.status === 200) {
                dispatch(slice?.actions?.storeMergeTagValueForTreatment(response?.data ?? {}));
            } else {
                dispatch(slice?.actions?.storeMergeTagValueForTreatment({}));
                const msg = _mergeTagError(`/treatments/${treatmentId}/get_context/`, response);
                dispatch(updateError({ success: "warning", message: msg }));
            }
        } catch (e) {
            dispatch(slice?.actions?.storeMergeTagValueForTreatment({}));
            dispatch(updateError({ success: "warning", message: _mergeTagError('/treatments/get_context/', null, e) }));
        }
    }
}
